import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { ButtonLink } from './shared/ButtonLink';
import type { PromoBannerComponent as PromoBannerComponentType } from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getFontFamily } from '../utils/fontLoader';
import { getThemeStyles } from '../styles/themes';
import { renderTextWithLineBreaks } from '../utils/textFormatting';

interface PromoBannerComponentProps {
  component: PromoBannerComponentType;
  isEditing?: boolean;
}

export const PromoBannerComponent: React.FC<PromoBannerComponentProps> = ({
  component,
  isEditing = false,
}) => {
  const { data } = component;
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);
  const brandFonts = useBrandFonts();

  // Font styles for body text (promo banner has no headings)
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Dismiss state - persisted in localStorage per component ID
  // In edit mode, always show the banner (ignore dismiss state)
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Only check localStorage in non-edit mode
    if (!isEditing && data.dismissible) {
      const dismissedKey = `promo-banner-dismissed-${component.id}`;
      const dismissed = localStorage.getItem(dismissedKey);
      if (dismissed === 'true') {
        setIsDismissed(true);
      }
    }
  }, [component.id, data.dismissible, isEditing]);

  const handleDismiss = () => {
    // Don't persist dismiss in edit mode
    if (isEditing) return;

    const dismissedKey = `promo-banner-dismissed-${component.id}`;
    localStorage.setItem(dismissedKey, 'true');
    setIsDismissed(true);
  };

  // Don't render if dismissed (only in non-edit mode)
  if (isDismissed && !isEditing) {
    return null;
  }

  // Color priority: customColors > theme colors > fallback
  const bgColor =
    (data as any).customColors?.background || themeStyles.promoBanner?.background || '#3b82f6';
  const textColor = (data as any).customColors?.text || themeStyles.promoBanner?.text || '#ffffff';
  const linkTextColor =
    (data as any).customColors?.linkText || themeStyles.promoBanner?.linkText || '#ffffff';
  const dismissButtonColor =
    (data as any).customColors?.dismissButton ||
    themeStyles.promoBanner?.dismissButton ||
    '#ffffff';

  // Check if background is a gradient
  const isGradient =
    bgColor?.startsWith('linear-gradient') || bgColor?.startsWith('radial-gradient');

  const backgroundStyle = isGradient ? { backgroundImage: bgColor } : { backgroundColor: bgColor };

  return (
    <div className="w-full py-2.5 px-4 relative" style={backgroundStyle}>
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-4">
        {/* Main text */}
        <span
          className="text-sm font-medium"
          style={{ color: textColor, ...bodyFontStyle }}
          dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.text) }}
        />

        {/* Link/CTA */}
        {data.linkText && data.linkUrl && (
          <ButtonLink
            url={data.linkUrl}
            target={data.linkTarget}
            className="text-sm font-semibold underline hover:no-underline transition-all"
            style={{ color: linkTextColor, ...bodyFontStyle }}
          >
            {data.linkText}
          </ButtonLink>
        )}

        {/* Dismiss button */}
        {data.dismissible && (
          <button
            onClick={handleDismiss}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-black/10 transition-colors"
            style={{ color: dismissButtonColor }}
            aria-label="Dismiss banner"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
};
